/* eslint-disable no-useless-escape */
import { Component, EventEmitter, OnInit, Output, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../auth/auth.service';
import { UtilsService } from '../utils.service';
import { GetUserQuery, RoleEnum, CreateUserInput, APIService, GetEntityQuery, GetRoleQuery } from '../../API.service';
import { EntityService } from '../entity.service';
import { NGXLogger } from 'ngx-logger';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../user.service';
import { AppLevelRoleEnum } from '../enums/appLevelRoles.enum';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { RootEntityContextService } from '../root-entity-context.service';

@Component({
  selector: 'cygov-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
  standalone: false,
})
export class AddUserModalComponent implements OnInit {
  @Input() fromVendor = false;
  @Output() modalResult = new EventEmitter<CreateUserInput>();
  entity: GetEntityQuery;
  subEntity: GetEntityQuery;
  subEntityList: GetEntityQuery[];
  currentUser: GetUserQuery;
  newUser: CreateUserInput;
  roleEnum = RoleEnum;
  roleOptions: string[] = [];
  selectedEntity: string;
  leaderTypes = ['SubEntityLeader'];
  leaderType;

  currentUserRole: GetRoleQuery; // Adding currentUserRole which will be removed and replaced with user.role after making connection.

  constructor(
    private authService: AuthService,
    private api: APIService,
    private entityService: EntityService,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private logger: NGXLogger,
    public activeModal: NgbActiveModal,
    private userService: UserService,
    private customApi: CUSTOMAPIService,
    private rootEntityContextService: RootEntityContextService
  ) {}

  async ngOnInit() {
    this.currentUser = await this.userService.getCurrentUser();
    this.currentUserRole = this.userService.getCurrentRole();
    this.newUser = this.initUser();
    const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    this.entity = entityId ? await this.customApi.GetEntity(entityId) : null;
    this.roleOptions = this.populateRoleOptions();
    try {
      if (this.entity) {
        if (this.entityService.isChildEntity(this.entity)) {
          if (await this.authService.hasPermission(RoleEnum.LEADER, this.entity.parentId)) {
            this.subEntityList = await this.rootEntityContextService.getSubEntities('CHILD_ENTITY');
            this.leaderTypes = [...this.leaderTypes, 'EntityLeader'];
          } else {
            this.subEntityList = [this.entity];
            this.newUser.role = RoleEnum.LEADER;
            this.selectedEntity = this.entity.id;
          }
        } else if (this.entityService.isRootEntity(this.entity)) {
          this.subEntityList = await this.rootEntityContextService.getSubEntities('CHILD_ENTITY');
          this.leaderTypes = [...this.leaderTypes, 'EntityLeader'];
        } else {
          this.toastrService.error('Unauthorized');
        }
      }
    } catch (e) {
      this.logger.error('AddUserModalComponent - Error: ', e);
      const message = UtilsService.msgFromError(e);
      this.toastrService.error(message);
    }
  }

  initUser(): any {
    return {
      name: '',
      email: '',
      role: AppLevelRoleEnum.ENTITY_LEADER,
      isCognitoUser: false,
      questionMap: JSON.stringify({}),
    };
  }

  getDisplayEntityId() {
    return this.entityService.isChildEntity(this.entity) ? this.entity.parentId : this.entity.id;
  }

  getPermissions() {
    switch (this.newUser.role) {
      case RoleEnum.LEADER:
        if (this.leaderType === this.leaderTypes[0]) {
          return [this.entity.id];
        } else {
          return [this.subEntity.id];
        }
      case RoleEnum.VENDOR:
        return [this.entity.id];
      case RoleEnum.PARTICIPANT:
        return [this.subEntity.id];
      default:
        return [];
    }
  }

  isValid(user: CreateUserInput) {
    let valid = true;
    const re =
      // eslint-disable-next-line max-len
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!re.test(String(user.email).toLowerCase())) {
      this.toastrService.error('Email is incorrect');
      valid = false;
    }
    if (!user.name) {
      this.toastrService.error('User name is required');
      valid = false;
    }
    if (!user.role) {
      this.toastrService.error('User role is required');
      valid = false;
    }
    if (user.role === RoleEnum.LEADER && !user.entityId) {
      this.toastrService.error('Please select sub entity');
      valid = false;
    }

    return valid;
  }

  emitUser(newUser: CreateUserInput) {
    newUser.email = newUser.email.toLowerCase();
    if (this.leaderType === this.leaderTypes[0] && this.selectedEntity) {
      newUser.entityId = this.selectedEntity;
    } else if (this.leaderType === this.leaderTypes[1]) {
      newUser.entityId = this.entity.id;
    }
    if (this.isValid(newUser)) {
      this.modalResult.emit(newUser);
    }
  }

  showSubEntityList(newUserRole) {
    return newUserRole === RoleEnum.LEADER && this.subEntityList && this.leaderType === this.leaderTypes[0];
  }

  populateRoleOptions(): string[] {
    const options: string[] = [];

    switch (this.currentUserRole.name.toLowerCase()) {
      case AppLevelRoleEnum.ADMIN:
        options.push(AppLevelRoleEnum.ADMIN);
        options.push(AppLevelRoleEnum.MSSP);
        options.push(AppLevelRoleEnum.ENTITY_LEADER);
        options.push(AppLevelRoleEnum.SUBENTITY_LEADER);
        break;
      case AppLevelRoleEnum.MSSP:
        options.push(AppLevelRoleEnum.MSSP);
        options.push(AppLevelRoleEnum.ENTITY_LEADER);
        options.push(AppLevelRoleEnum.SUBENTITY_LEADER);
        break;
      case AppLevelRoleEnum.ENTITY_LEADER:
        options.push(AppLevelRoleEnum.ENTITY_LEADER);
        options.push(AppLevelRoleEnum.SUBENTITY_LEADER);
        break;
      case AppLevelRoleEnum.SUBENTITY_LEADER:
        options.push(AppLevelRoleEnum.SUBENTITY_LEADER);
        break;
    }
    return options;
  }
}
